import React, { useEffect, useRef, useState } from 'react';

/**
 * One type-ahead over the whole material library: paint colours from the
 * curated catalogue plus anything imported (wallpaper, tile, fabric...).
 *
 * Searching happens on the server, so it works the moment a catalogue is
 * imported and does not need the whole library shipped to the browser.
 *
 * props:
 *   onSelect(material)  - chosen row: { name, sku, manufacturer, category, image_url, size }
 *   category            - restrict to one category ('wallpaper', 'tile', ...)
 */
const MaterialSearchField = ({
  onSelect,
  category = null,
  placeholder = 'Type a paint colour, wallpaper, tile…',
  autoFocus = false,
  compact = false,
  testId = 'material-search',
}) => {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);

  const backendUrl = () => (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 1) { setHits([]); return undefined; }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        // Paint lives in the curated catalogue; everything else in the library.
        const urls = [];
        if (!category || category === 'paint') {
          urls.push(`${backendUrl()}/api/autocomplete/paint-colors?q=${encodeURIComponent(term)}&limit=8`);
        }
        if (category !== 'paint') {
          const cat = category ? `&category=${encodeURIComponent(category)}` : '';
          urls.push(`${backendUrl()}/api/autocomplete/materials?q=${encodeURIComponent(term)}&limit=8${cat}`);
        }

        const results = await Promise.all(urls.map((u) =>
          fetch(u).then((r) => (r.ok ? r.json() : [])).catch(() => [])
        ));
        if (cancelled) return;

        const merged = [];
        const seen = new Set();
        results.flat().forEach((row) => {
          if (!row || !row.name) return;
          const key = `${row.manufacturer || ''}|${row.sku || ''}|${row.name}`.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          merged.push(row);
        });
        setHits(merged);
        setOpen(true);
        setHighlight(-1);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 180);   // debounce: one request per pause, not per keystroke

    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, category]);

  const choose = (row) => {
    setQuery('');
    setHits([]);
    setOpen(false);
    if (onSelect) onSelect(row);
  };

  const onKeyDown = (e) => {
    if (!open || !hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((i) => Math.min(i + 1, hits.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((i) => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      choose(hits[highlight >= 0 ? highlight : 0]);
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => hits.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        autoFocus={autoFocus}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        className={`w-full rounded bg-black/50 border border-white/15 text-white placeholder-white/30 focus:border-[#D4A574] outline-none ${compact ? 'text-[10px] px-2 py-1' : 'text-xs px-3 py-2'}`}
      />

      {busy && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-white/40">…</span>
      )}

      {open && (
        <div
          data-testid={`${testId}-results`}
          className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-[#D4A574]/50 overflow-hidden shadow-2xl"
          style={{ background: '#14141c', maxHeight: 280, overflowY: 'auto' }}
        >
          {hits.length === 0 ? (
            <div className="px-3 py-2 text-[10px] text-white/40" data-testid={`${testId}-empty`}>
              Nothing found. Paint is built in; wallpaper and tile catalogues need importing first.
            </div>
          ) : hits.map((row, i) => (
            <button
              key={`${row.manufacturer}-${row.sku}-${row.name}-${i}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); choose(row); }}
              onMouseEnter={() => setHighlight(i)}
              data-testid={`${testId}-option-${i}`}
              className={`w-full text-left flex items-center gap-2 px-2 py-1.5 ${highlight === i ? 'bg-[#D4A574]/25' : 'hover:bg-white/5'}`}
            >
              {row.image_url ? (
                <img src={row.image_url} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
              ) : (
                <span
                  className="w-7 h-7 rounded flex-shrink-0 border border-white/10"
                  style={{ background: row.swatch_color || '#2a2a33' }}
                />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-bold text-white truncate">{row.name}</span>
                <span className="block text-[9px] text-white/45 truncate">
                  {[row.manufacturer, row.sku, row.collection, row.size].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="text-[8px] font-black uppercase text-[#D4A574]/70 flex-shrink-0">
                {row.category || ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaterialSearchField;
